import {combineLatest, EMPTY, forkJoin, Observable, of, ReplaySubject, Subject} from 'rxjs';
import {Injectable} from '@angular/core';
import {DeviceSlotId, ProjectId} from '../../types/ids';
import {isDefined, isNoUUID} from '../../utils/utils';
import {Deployable, DeviceOfferId} from '../../types/devices/device-template';
import {DeviceApi} from '../../api/rest/device.restapi';
import {distinctUntilChanged, finalize, map, mergeMap, shareReplay, startWith, switchMap, take, tap} from 'rxjs/operators';
import {Browser} from '@webmate-web-core/shared/types/browser';
import {DeviceService} from "@webmate-web-core/shared/services/device/device.service";
import {DeviceSlotService} from "@webmate-web-core/shared/services/device/device-slot.service";
import {DeviceSlot} from '@webmate-web-core/shared/types/devices/device-slot';
import {getPlatformFamily, PlatformFamily} from '@webmate-web-core/shared/types/devices/device-properties';
import {UserService} from '@webmate-web-core/shared/services/user.service';
import {compareVersionNumbers} from '@webmate-web-core/shared/utils/utils';

interface DeployableSlotQuery {
    slotId: DeviceSlotId;
    generation: number;
}

/**
 * Repository managing Device Templates.
 */
@Injectable({
    providedIn: 'root'
})
export class DeployableService {

    private _deployableCache: {[s: string /* DeviceSlotId*/]: {[s: string /* DeviceOfferId*/]: ReplaySubject<Deployable>}} = {};

    /** Increased whenever the offers of a slot are invalidated. It distinguishes requests made before and after an
     * update even when the set of eligible slot ids stays unchanged. */
    private _cacheGenerationBySlot: {[s: string /* DeviceSlotId*/]: number} = {};
    private _slotInvalidations = new Subject<DeviceSlotId>();

    /** running queries per slot, so that parallel consumers (e.g. the desktop and the mobile device view plus the
     * deployment dialog) share one request per slot instead of each firing their own */
    private _runningQueryBySlot: {[s: string /* DeviceSlotId*/]: Observable<Deployable[]>} = {};

    constructor(private deviceApi: DeviceApi, private slotService: DeviceSlotService, private userService: UserService,
                private deviceService: DeviceService) {
        /* Whenever a slot changes (most notably when a device is deployed into it or released from it), the offers we
         * cached for that slot are outdated. Dropping them here is the single point of invalidation for every consumer,
         * the slot summaries as well as the deployment dialog. This service is a root-singleton, hence no teardown. */
        this.deviceService.handleDeviceSlotUpdateEvents().subscribe(evt => this.invalidateSlot(evt.deviceSlotId));
    }

    /** Drops the cached offers of the given slot, so that the next query obtains fresh ones from the backend. */
    invalidateSlot(slotId: DeviceSlotId): void {
        if (isDefined(slotId)) {
            this._cacheGenerationBySlot[slotId] = this.getCacheGeneration(slotId) + 1;
            delete this._deployableCache[slotId];
            /* a query that is still running was started before the invalidation, so its answer is stale as well and
             * must not be shared with the callers that come after the invalidation */
            delete this._runningQueryBySlot[slotId];
            this._slotInvalidations.next(slotId);
        }
    }

    private getCacheGeneration(slotId: DeviceSlotId): number {
        return this._cacheGenerationBySlot[slotId] ?? 0;
    }

    /**
     * Returns a list of deployables that are available in the given Project.
     *
     * @param platformFamily If provided, only deployables for the given platform family are returned. Slots that
     *                       cannot serve that family are not even queried.
     * Note that headless slots are never queried: their offers cannot be used for interactive device tests and are
     * discarded by every consumer anyway.
     * TODO: provide more efficient API
     */
    getDeployablesForProject$(projectId: ProjectId, platformFamily?: string, filterReservations: boolean = false): Observable<Array<Deployable>> {
        // TODO: use new deployment API with filter
        return this.userService.getCurrentUserData().pipe(
            take(1),
            map(user => user?.id),
            switchMap(currentUserId => {
                const eligibleSlots = this.slotService.getAvailableSlotsForProject$(projectId).pipe(
                    map(slots => slots.filter(slot => !slot.isHiddenSlot())),
                    map(slots => {
                        if (!filterReservations) {
                            return slots;
                        }
                        return slots.filter(slot => {
                            const active = slot.reservations?.filter(r => r.isActive) ?? [];
                            if (active.length === 0) {
                                return true;
                            }
                            const notOwnReservation= active.some(r => r.userId !== currentUserId);
                            return !notOwnReservation;
                        });
                    }),
                    map(slots => slots.filter(slot => DeployableService.mayOfferDeployables(slot, platformFamily)))
                );
                return combineLatest([
                    eligibleSlots,
                    this._slotInvalidations.pipe(startWith(undefined as DeviceSlotId | undefined))
                ]).pipe(
                    map(([slots]) => slots.map(slot => ({
                        slotId: slot.id,
                        generation: this.getCacheGeneration(slot.id)
                    }))),
                    /* Compare both membership and invalidation state: unrelated slot updates are suppressed, while an
                     * availability update can refresh offers even if all eligible slot ids remain unchanged. */
                    distinctUntilChanged((a, b) => DeployableService.sameSlotQueries(a, b)),
                    switchMap(slotQueries => {
                        const deployables = slotQueries
                            .map(slot => this.getDeployablesForSlot$(slot.slotId).pipe(take(1)));
                        if (deployables.length === 0) {
                            return of([] as Deployable[]);
                        }
                        return forkJoin(deployables).pipe(map(deployables => {
                            let result = [];
                            deployables
                                // flatten array
                                .reduce((acc, val) => acc.concat(val), [])
                                .filter(depl => !platformFamily || getPlatformFamily(depl.properties) === platformFamily)
                                .forEach(deployable => {
                                    result = result.concat(deployable);
                                });
                            return result;
                        }));
                    })
                );
            })
        )
    }

    /** decides whether a slot can contribute offers, i.e. whether it is worth a request at all */
    private static mayOfferDeployables(slot: DeviceSlot, platformFamily?: string): boolean {
        if (slot.deviceId !== undefined || !slot.availableForDeployment) {
            return false; // occupied or blocked slots offer nothing that could be deployed
        }
        if (slot.isHeadlessSlot()) {
            return false;
        }
        // slots whose requirements do not reveal a family (custom slots) are queried, they may serve anything
        return !platformFamily || (slot.getPlatformFamilyHint() ?? platformFamily) === platformFamily;
    }

    /** compares which generation of each slot's offers would be queried */
    private static sameSlotQueries(a: DeployableSlotQuery[], b: DeployableSlotQuery[]): boolean {
        const sortedA = a.slice().sort((x, y) => x.slotId.localeCompare(y.slotId));
        const sortedB = b.slice().sort((x, y) => x.slotId.localeCompare(y.slotId));
        return sortedA.length === sortedB.length && sortedA.every((slot, i) =>
            slot.slotId === sortedB[i].slotId && slot.generation === sortedB[i].generation);
    }

    getLatestBrowserVersionForProject$(projectId: ProjectId, browserType: string, platformFamily?: PlatformFamily): Observable<Browser | undefined> {
        return this.getDeployablesForProject$(projectId, platformFamily, true).pipe(
            map(deployables => deployables
                .flatMap(deployable => deployable.browser)
                .filter(browser => browser.browserType.toLowerCase() === browserType.toLowerCase())
                .reduce((latest: Browser | undefined, browser) => {
                    if (!latest) {
                        return browser;
                    }
                    return compareVersionNumbers(browser.version, latest.version) > 0 ? browser : latest;
                }, undefined))
        );
    }

    /** Returns a list of Ids of all the Device Templates that are available in the given Project. */
    getDeployablesForSlot$(slotId: DeviceSlotId, forceReload: boolean = false): Observable<Array<Deployable>> {
        if (isNoUUID(slotId)) throw new ReferenceError(`'getDeployablesForSlot$' called with an illegal ID: '${slotId}'`);
        /* an empty cache-entry is not a valid answer: it is what we obtain while the slot is occupied and it would
         * otherwise keep this slot without any offers until the page is reloaded */
        if (forceReload || !this._deployableCache[slotId] || Object.keys(this._deployableCache[slotId]).length === 0) {
            if (this._runningQueryBySlot[slotId]) {
                return this._runningQueryBySlot[slotId]; // a query for this slot is already on its way, share it
            }
            const requestGeneration = this.getCacheGeneration(slotId);
            const query = this.deviceApi.getDeployables(slotId).pipe(
                tap(deployables => {
                    /* An invalidation may have installed a replacement while this request was in flight. Its callers
                     * may still receive the response, but obsolete data must never replace the current cache. */
                    if (requestGeneration !== this.getCacheGeneration(slotId) || this._runningQueryBySlot[slotId] !== query) {
                        return;
                    }
                    // Reset cache as DeployableIds are temporary
                    this._deployableCache[slotId] = {};
                    deployables.forEach(d => {
                        if(!this._deployableCache[slotId][d.id]) {
                            this._deployableCache[slotId][d.id] = new ReplaySubject<Deployable>(1);
                        }
                        this._deployableCache[slotId][d.id].next(d);
                    });
                }),
                // only withdraw our own registration, a newer query may have replaced it in the meantime
                finalize(() => { if (this._runningQueryBySlot[slotId] === query) delete this._runningQueryBySlot[slotId]; }),
                shareReplay({bufferSize: 1, refCount: false})
            );
            this._runningQueryBySlot[slotId] = query;
            return query;
        }
        let d = Object.values(this._deployableCache[slotId]).map(x => x.asObservable());
        return d.length > 0 ? combineLatest(d) : of([]);
    }

    getDeployable$(slotId: DeviceSlotId, id: DeviceOfferId): Observable<Deployable> {
        //TODO invalidate offers after a certain time
        if (isDefined(this._deployableCache[slotId]) && isDefined(this._deployableCache[slotId][id])) {
            return this._deployableCache[slotId][id].asObservable();
        } else {
            return this.getDeployablesForSlot$(slotId).pipe(mergeMap(() => {
                /* the query does not necessarily fill the cache: the slot may not offer this deployable (any more) and
                 * an answer that was obsoleted while it was on its way does not touch the cache at all */
                const cachedDeployable = this._deployableCache[slotId]?.[id];
                return cachedDeployable !== undefined ? cachedDeployable.asObservable() : EMPTY;
            }));
        }
    }

}
